import { Box, Stack } from '@mui/material';
import { Button, Tooltip, Typography, colors } from '@sweep-io/sweep-design';
import { ChevronDown } from '@sweep-io/sweep-design/dist/icons/ChevronDown';
import { ChevronUp } from '@sweep-io/sweep-design/dist/icons/ChevronUp';
import { Close } from '@sweep-io/sweep-design/dist/icons/Close';
import type { ReactNode, MouseEvent } from 'react';
import { useState } from 'react';
import type { SweepTypographyVariants } from '@sweep-io/sweep-design/dist/components/Typography/types';
import { useFeatureToggle } from '../../feature-flags/useFeatureToggle';

interface FilterButtonsProps {
  isPressed: boolean;
  isOpen?: boolean;
  text: string;
  preText?: ReactNode;
  title?: string;
  tooltip?: string;
  handleClick: (event: MouseEvent<HTMLElement>) => any;
  showClearBtn?: boolean;
  onClearBtn?: () => any;
  disabled?: boolean;
  dataTestId?: string;
  maxWidth?: string;
  titleVariant?: SweepTypographyVariants;
  titleColor?: string;
}

export const FilterButton = ({
  handleClick,
  isPressed,
  isOpen,
  text,
  title,
  tooltip,
  showClearBtn,
  onClearBtn,
  disabled,
  preText,
  dataTestId,
  maxWidth,
  titleVariant = 'body',
  titleColor,
}: FilterButtonsProps) => {
  const { newAdvancedFilterDesign } = useFeatureToggle();
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const isUserInteraction = isPressed || isHovered || isActive;
  const iconColor = disabled ? colors.grey[500] : isUserInteraction ? colors.blue[500] : undefined;
  const shouldShowTitle = Boolean(title) && !newAdvancedFilterDesign;

  return (
    <Stack gap={0.5} direction="row" alignItems="center" minWidth={0}>
      {shouldShowTitle && (
        <Typography variant={titleVariant} whiteSpace="nowrap" color={titleColor}>
          {title}:
        </Typography>
      )}
      <Box
        sx={{
          display: 'inline-flex',
          minWidth: 0,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseDown={() => setIsActive(true)}
        onMouseUp={() => setIsActive(false)}
      >
        <Button
          variant="system"
          size="tiny"
          onClick={handleClick}
          pressed={isPressed}
          disabled={disabled}
          endIcon={showClearBtn ? undefined : (isOpen ?? isPressed) ? ChevronUp : ChevronDown}
        >
          <Box sx={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
            <Tooltip title={tooltip} placement="top">
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                  overflow: 'hidden',
                  minWidth: 0,
                  maxWidth,
                }}
                data-testid={dataTestId}
              >
                {preText}
                <Typography variant="caption-medium" whiteSpace="nowrap" ellipsis={true}>
                  {text}
                </Typography>
              </Box>
            </Tooltip>
          </Box>
          {showClearBtn && (
            <Box
              sx={{ display: 'flex', pl: 0.5, alignItems: 'baseline' }}
              onClick={(e) => {
                e.stopPropagation();
                onClearBtn && onClearBtn();
              }}
            >
              <Close size="small" color={iconColor} />
            </Box>
          )}
        </Button>
      </Box>
    </Stack>
  );
};
